import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Printer, PencilSimple, FilePdf, SealCheck, LockKeyOpen } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { useAuth, canVerify } from '@/auth/AuthContext';
import { STAGE_ORDER, STAGE_BADGE, STAGE_ACTION, computeStage, eligibleStage } from '@/lib/workflow';
import VerificationOverlay from '@/components/verify/VerificationOverlay';
import OvertimePrintSheet from '@/components/print/OvertimePrintSheet';
import LeavePrintSheet from '@/components/print/LeavePrintSheet';
import LeaveTransferPrintSheet from '@/components/print/LeaveTransferPrintSheet';
import WorkDeclarationPrintSheet from '@/components/print/WorkDeclarationPrintSheet';
import TravelExpensePrintSheet from '@/components/print/TravelExpensePrintSheet';
import TravelReceiptsPanel from '@/components/print/TravelReceiptsPanel';
import { formatDateTime } from '@/utils/datetime';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PAGE_CLASS = {
  overtime: 'print-overtime preview-overtime',
  leave: 'print-leave preview-leave',
  leave_transfer: 'print-leave-transfer preview-leave-transfer',
  work_declaration: 'print-work-declaration preview-work-declaration',
  travel_expense: 'print-travel preview-travel',
};

const PAGE_NAME = {
  overtime: 'overtime-page',
  leave: 'leave-page',
  leave_transfer: 'leave-transfer-page',
  work_declaration: 'work-declaration-page',
  travel_expense: 'travel-page',
};

function renderSheet(submission) {
  switch (submission.form_type) {
    case 'overtime': return <OvertimePrintSheet submission={submission} />;
    case 'leave': return <LeavePrintSheet submission={submission} />;
    case 'leave_transfer': return <LeaveTransferPrintSheet submission={submission} />;
    case 'work_declaration': return <WorkDeclarationPrintSheet submission={submission} />;
    case 'travel_expense': return <TravelExpensePrintSheet submission={submission} />;
    default: return <div>未対応のフォーム形式</div>;
  }
}

export default function ViewSubmission() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [submission, setSubmission] = useState(null);
  const [stages, setStages] = useState(STAGE_ORDER);
  const [wfEnabled, setWfEnabled] = useState(true);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const [subRes, cfgRes] = await Promise.all([
          axios.get(`${API}/forms/submissions/${id}`),
          axios.get(`${API}/workflow-config`).catch(() => null),
        ]);
        setSubmission(subRes.data);
        setWfEnabled(cfgRes?.data?.enabled !== false);
        const forms = cfgRes?.data?.forms || [];
        const cfg = forms.find((f) => f.form_type === subRes.data.form_type);
        if (cfg?.stages?.length) setStages(cfg.stages);
      } catch (error) {
        console.error('Failed to fetch submission:', error);
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const handlePrint = () => {
    // Print synchronously (Chrome requires the user gesture); mark-printed in background.
    axios.post(`${API}/forms/submissions/mark-printed`, { ids: [id] })
      .then(() => setSubmission((prev) => prev ? { ...prev, last_printed_at: new Date().toISOString() } : prev))
      .catch((e) => console.warn('mark-printed failed', e));
    window.print();
  };

  // Admin "release" of a finally-approved submission: removes the 承認 stage so the
  // form drops back to 承認待ち and can be edited / re-approved (redo).
  const handleRelease = async () => {
    if (!window.confirm('この申請の承認を解除します。署名・確認は残りますが、承認待ちに戻り、編集・再承認のやり直しが可能になります。よろしいですか？')) return;
    try {
      const res = await axios.delete(`${API}/forms/submissions/${id}/workflow/approve`);
      setSubmission(res.data);
      toast.success('承認を解除しました。編集・再承認が可能です。');
    } catch (e) {
      toast.error(e.response?.data?.detail || '承認の解除に失敗しました');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <p className="text-zinc-500">読み込み中...</p>
      </div>
    );
  }

  if (!submission) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <p className="text-zinc-500">申請が見つかりません</p>
      </div>
    );
  }

  const stage = computeStage(submission, stages);
  const badge = STAGE_BADGE[stage] || STAGE_BADGE.awaiting_sign;
  const elig = eligibleStage(user, submission, stages);
  const verifCount = (submission.verifications || []).length;
  const fullyApproved = stage === 'approved';
  // Stages the current user signed and may still re-edit (1a): own signature (or
  // admin), and only while the submission is not yet finally approved.
  const myEditableStages = (!user || fullyApproved)
    ? []
    : (submission.verifications || [])
        .filter((v) => (user.role === 'admin' || v.user_id === user.id) && stages.includes(v.stage))
        .map((v) => v.stage)
        .sort((a, b) => stages.indexOf(a) - stages.indexOf(b));

  return (
    <div>
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between no-print">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          <Button variant="outline" onClick={() => navigate('/history')} data-testid="back-button" className="border-zinc-300">
            <ArrowLeft size={18} className="mr-2" />
            戻る
          </Button>
          {submission.last_printed_at && (
            <span className="text-xs text-zinc-500" data-testid="last-printed-info">
              最終印刷日: {formatDateTime(submission.last_printed_at)}
            </span>
          )}
          {wfEnabled && (
          <span
            className={`inline-flex items-center gap-1.5 text-xs font-semibold rounded-full border px-2.5 py-1 ${badge.cls}`}
            data-testid="stage-badge"
          >
            <SealCheck size={14} weight="fill" />
            {badge.label}
            {verifCount > 0 && stage !== 'approved' && (
              <span className="font-normal opacity-70">・{verifCount}/{stages.length} 完了</span>
            )}
          </span>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2 shrink-0">
          {wfEnabled && (elig ? (
            <Button
              onClick={() => navigate(`/verify/${id}`)}
              data-testid="action-verify-btn"
              className="bg-teal-700 hover:bg-teal-800"
            >
              <SealCheck size={18} className="mr-2" />
              {STAGE_ACTION[elig].verb}
            </Button>
          ) : (stage !== 'approved' && canVerify(user)) ? (
            <Button
              variant="outline"
              onClick={() => navigate(`/verify/${id}`)}
              data-testid="action-verify-btn"
              className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
            >
              <SealCheck size={18} className="mr-2" />
              検証画面
            </Button>
          ) : null)}
          {wfEnabled && fullyApproved && user?.role === 'admin' && (
            <Button
              variant="outline"
              onClick={handleRelease}
              data-testid="action-release-btn"
              className="border-amber-300 text-amber-700 hover:bg-amber-50"
            >
              <LockKeyOpen size={18} className="mr-2" />
              承認を解除
            </Button>
          )}
          {wfEnabled && myEditableStages.map((s) => (
            <Button
              key={s}
              variant="outline"
              onClick={() => navigate(`/verify/${id}?stage=${s}`)}
              data-testid={`action-edit-stage-${s}`}
              className="border-violet-300 text-violet-700 hover:bg-violet-50"
            >
              <PencilSimple size={18} className="mr-2" />
              {(STAGE_ACTION[s]?.label || s)}を編集
            </Button>
          ))}
          <Button
            variant="outline"
            onClick={() => navigate(`/submission/${id}/edit`)}
            data-testid="action-edit-btn"
            className="border-zinc-300 text-amber-700 hover:bg-amber-50"
          >
            <PencilSimple size={18} className="mr-2" />
            編集
          </Button>
          <Button onClick={handlePrint} data-testid="action-print-btn" className="bg-teal-700 hover:bg-teal-800">
            <Printer size={18} className="mr-2" />
            印刷
          </Button>
          <Button
            onClick={() => navigate(`/print?ids=${id}&mode=pdf`)}
            data-testid="action-pdf-btn"
            className="bg-rose-600 hover:bg-rose-700"
          >
            <FilePdf size={18} className="mr-2" />
            PDFで保存
          </Button>
        </div>
      </div>

      <div className="print-container">
        {/* Root-named page kills Chromium's trailing blank page (see PrintPage) */}
        <style>{`@media print { html, body, #root { page: ${PAGE_NAME[submission.form_type] || 'auto'}; } }`}</style>
        <div className={`relative ${PAGE_CLASS[submission.form_type] || ''}`}>
          {renderSheet(submission)}
          <VerificationOverlay verifications={submission.verifications} />
        </div>
        {submission.form_type === 'travel_expense' && (
          <TravelReceiptsPanel submission={submission} />
        )}
      </div>
    </div>
  );
}
